import React, { memo, useCallback } from 'react';
import { Button, Table } from 'antd';
import Loader from '@components/Loader';
import { setAsset, setAssetVariant } from '@redux/actions/mediaViewerAction';
import { nonNull } from '@helpers/non-null';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import { ReactComponent as FolderSvg } from '@assets/icons/folder.svg';
import { ReactComponent as PrivateFolderSvg } from '@assets/icons/folder-private.svg';
import moment from 'moment';
import { AssetItemDto } from '@api/Api';
import UserAvatar from '@components/UserAvatar';
import type { FolderWithItems } from '@pages/Campaigns/CampaignReview/utils';
import { useDispatch } from 'react-redux';
import Status from '@components/Approval/Status';
import { download, downloadAll } from '@helpers/download';
import { ReactComponent as DownloadInvoiceSvg } from '@assets/icons/download-invoice.svg';

import './ListView.less';

type ListViewProps = {
  onSelectFolder: (folderId: string) => void;
  folders: FolderWithItems[];
  assetsList: AssetItemDto[];
  isOpenFolder: boolean;
};

export default memo(function ListView({
  onSelectFolder,
  folders,
  assetsList,
  isOpenFolder
}: ListViewProps) {
  const dispatch = useDispatch();

  const onAssetClick = useCallback(
    (asset: AssetItemDto) => {
      const selectedAsset = nonNull(asset.asset).versions[0];
      dispatch(setAsset(asset));
      dispatch(setAssetVariant(selectedAsset.id));
    },
    [dispatch]
  );

  const foldersColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 480,
      render: (name: string, { id, isPrivate }: FolderWithItems) => {
        return (
          <div className="list-view-folder">
            <div
              className="list-view-folder-icon"
              onClick={() => {
                onSelectFolder(id);
              }}
            >
              {isPrivate ? <PrivateFolderSvg /> : <FolderSvg />}
            </div>
            {name}
          </div>
        );
      }
    },
    {
      title: '',
      dataIndex: 'owner',
      key: 'owner',
      width: 190
    },
    {
      title: ' ',
      dataIndex: 'created',
      key: 'created',
      width: 190
    },
    {
      title: 'Modified',
      key: 'modified',
      dataIndex: 'modified',
      width: 160,
      render: (_id: string, folder: FolderWithItems) => {
        const modified = Math.max(
          ...folder.items.map((x) => new Date(x.modifiedAt).getTime())
        );
        return moment(modified).fromNow();
      }
    },
    {
      title: ' ',
      key: 'actions',
      dataIndex: 'id',
      width: 32,
      render: (_id: string, folder: FolderWithItems) => {
        const assetsDownloadUrls = folder.items
          .filter((asset) => !!asset.asset?.permissions.download)
          .map((asset) => asset.asset?.versions[0].sourceUrl);
        return (
          <Button
            type="text"
            size="small"
            className="assets-list-btn-download"
            disabled={!assetsDownloadUrls?.length}
            icon={<DownloadInvoiceSvg />}
            onClick={(e) => {
              e.stopPropagation();
              if (!assetsDownloadUrls?.length) return;
              downloadAll(assetsDownloadUrls, `${folder.name}.zip`);
            }}
          />
        );
      }
    }
  ];

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 480,
      render: (_name: string, { asset }: AssetItemDto) => {
        const preparePreview = assetPreviewThumbnails({
          url: asset?.versions[0].metadata.preview?.url || '',
          status: asset?.versions[0].metadata.preview?.status || 'ready',
          type: asset?.versions[0].metadata.type || '',
          size: 40,
          format: 'img'
        });
        return (
          <div className="list-view-asset">
            <div className="list-view-asset-icon">
              {preparePreview === 'pending' && (
                <Loader spinSize={40} isSpinning={true} />
              )}
              {preparePreview !== 'pending' &&
              typeof preparePreview === 'string' ? (
                <img src={preparePreview || ''} alt="" />
              ) : (
                preparePreview
              )}
            </div>
            <div className="list-view-asset-name">
              {asset?.versions?.[0]?.name}.{asset?.versions?.[0]?.extension}
              {!!asset?.approvalWorkflow && (
                <Status
                  showOverdueTag
                  status={asset.approvalWorkflow.status}
                  approvalWorkflowStatus={asset.approvalWorkflow.status}
                  approvalWorkflowIsPaused={asset.approvalWorkflow.isPaused}
                  deadline={asset.approvalWorkflow.deadline}
                />
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 190,
      render: (_id: string, { owner }: AssetItemDto) => {
        return (
          <UserAvatar
            isActive={true}
            size={40}
            src={owner?.user?.picture.url || ''}
            userEmail={owner?.user?.email || ''}
            userName={owner?.user?.name || ''}
          />
        );
      }
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 190,
      render: (createdAt: string) => {
        return moment(createdAt).format('MMM DD, YYYY');
      }
    },
    {
      title: 'Modified',
      key: 'modifiedAt',
      dataIndex: 'modifiedAt',
      width: 160,
      render: (modifiedAt: string) => {
        return moment(modifiedAt).fromNow();
      }
    },
    {
      title: ' ',
      key: 'actions',
      dataIndex: 'id',
      width: 32,
      render: (_id: string, asset: AssetItemDto) => {
        return (
          <Button
            type="text"
            size="small"
            className="assets-list-btn-download"
            disabled={!asset.asset?.permissions.download}
            icon={<DownloadInvoiceSvg />}
            onClick={(e) => {
              e.stopPropagation();
              if (!asset.asset?.permissions.download) return;
              const { name, sourceUrl } = asset.asset.versions[0];
              download(sourceUrl, name);
            }}
          />
        );
      }
    }
  ];

  return (
    <div className="assets-list-view">
      {!!folders?.length && !isOpenFolder && (
        <div className="assets-list-view-folders">
          <div className="assets-list-view-label">Folders</div>
          <Table
            pagination={false}
            columns={foldersColumns}
            dataSource={folders}
            onRow={({ id }) => ({
              onClick: () => {
                onSelectFolder(id);
              }
            })}
          />
        </div>
      )}
      {!!assetsList?.length && (
        <div className="assets-list-view-assets">
          <div className="assets-list-view-label">Assets</div>
          <Table
            pagination={false}
            columns={columns}
            dataSource={assetsList}
            rowKey={({ id }) => id}
            onRow={(asset) => ({
              onClick: () => {
                onAssetClick(asset);
              }
            })}
          />
        </div>
      )}
    </div>
  );
});
